/**
 * LocalStorage Cleanup Utility
 * 
 * Provides functions to clean up sensitive data from localStorage
 * for security and privacy purposes, especially on logout.
 * 
 * Created: 2025-06-28
 * Purpose: Security enhancement - prevent data persistence after logout
 * Requirements summary: browser storage cleanup must tolerate blocked storage
 * so public auth/register pages do not crash on Mobile Safari privacy errors.
 */

import { createLogger } from '@/utils/logger/devLogger';
import {
  safeLocalStorageClear,
  safeLocalStorageKey,
  safeLocalStorageLength,
  safeLocalStorageRemoveItem,
} from '@/utils/storage/safeLocalStorage';
import { DEMO_REAL_ORG_STORAGE_KEY } from '@/utils/demo/demoConfig';

const logger = createLogger('LocalStorageCleanup');

// Requirement summary: Local auth cleanup must remove stale Supabase project
// sessions so localhost cannot mix hosted and local Docker auth tokens.

const SUPABASE_AUTH_COOKIE_PATTERN = /^sb-.*auth-token(?:\.\d+)?$/;

const getSupabaseStorageKey = (supabaseUrl: string): string | null => {
  try {
    const hostname = new URL(supabaseUrl).hostname;
    return hostname.endsWith('.supabase.co')
      ? hostname.split('.')[0]
      : hostname.split('.')[0].replace(/[^a-zA-Z0-9-]/g, '-');
  } catch {
    return null;
  }
};

const cleanupSupabaseAuthCookies = (): void => {
  if (typeof document === 'undefined') return;

  document.cookie.split(';').forEach((cookie) => {
    const cookieName = cookie.split('=')[0]?.trim();
    if (!cookieName || !SUPABASE_AUTH_COOKIE_PATTERN.test(cookieName)) return;

    document.cookie = `${cookieName}=; Max-Age=0; path=/; SameSite=Lax`;
  });
};

export const cleanupForeignSupabaseAuthStorage = (currentSupabaseUrl?: string): void => {
  if (typeof window === 'undefined' || !currentSupabaseUrl) return;

  const currentStorageKey = getSupabaseStorageKey(currentSupabaseUrl);
  if (!currentStorageKey) return;

  const currentPrefix = `sb-${currentStorageKey}-auth-token`;
  const localStorageKeysToRemove: string[] = [];

  try {
    for (let i = 0; i < safeLocalStorageLength(); i++) {
      const key = safeLocalStorageKey(i);
      if (key?.startsWith('sb-') && !key.startsWith(currentPrefix)) {
        localStorageKeysToRemove.push(key);
      }
    }

    localStorageKeysToRemove.forEach((key) => safeLocalStorageRemoveItem(key));

    document.cookie.split(';').forEach((cookie) => {
      const cookieName = cookie.split('=')[0]?.trim();
      if (!cookieName || !SUPABASE_AUTH_COOKIE_PATTERN.test(cookieName)) return;
      if (cookieName.startsWith(currentPrefix)) return;

      document.cookie = `${cookieName}=; Max-Age=0; path=/; SameSite=Lax`;
    });
  } catch (error) {
    logger.error('Error cleaning foreign Supabase auth storage:', error);
  }
};

/**
 * Clean up user-specific data from localStorage
 * 
 * This function removes sensitive user data that should not persist
 * after logout, especially important for shared devices.
 */
export const cleanupUserLocalStorage = (): void => {
  if (typeof window === 'undefined') {
    logger.warn('Window not available, skipping localStorage cleanup');
    return;
  }

  try {
    const keysToRemove: string[] = [];
    
    // Collect all localStorage keys that should be removed
    for (let i = 0; i < safeLocalStorageLength(); i++) {
      const key = safeLocalStorageKey(i);
      if (key) {
        // Remove Supabase auth/session caches, including stale hosted-project
        // sessions that can collide with local Docker Supabase on localhost.
        if (key.startsWith('sb-')) {
          keysToRemove.push(key);
        }

        // Remove active role data (security sensitive)
        if (key.startsWith('activeRole_')) {
          keysToRemove.push(key);
        }
        
        // Remove organization-specific data (privacy)
        if (key.startsWith('orgSelection_')) {
          keysToRemove.push(key);
        }
        
        // Remove user preferences that might contain sensitive info
        if (key.startsWith('userPrefs_')) {
          keysToRemove.push(key);
        }
        
        // Remove organization-specific cached data
        if (key.startsWith('schedule_events_')) {
          keysToRemove.push(key);
        }
        
        // Remove any other organization ID patterns (UUID format)
        if (key.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/)) {
          keysToRemove.push(key);
        }
      }
    }
    
    // Remove specific application data
    const specificKeysToRemove = [
      'athleteFilters',           // Saved filter preferences
      'parentFilters',            // Parent filter preferences
      'userFilters',              // User filter preferences
      'lastVisitedOrganization',  // Organization context
      'recentSearches',           // Search history
      'dashboardLayout',          // Layout preferences
      DEMO_REAL_ORG_STORAGE_KEY,  // Demo-mode "return to this gym" pointer
    ];
    
    // Remove all identified keys
    [...keysToRemove, ...specificKeysToRemove].forEach(key => {
      safeLocalStorageRemoveItem(key);
    });
    cleanupSupabaseAuthCookies();
    
    logger.info('Successfully cleaned up localStorage entries', {
      dynamicKeys: keysToRemove,
      staticKeys: specificKeysToRemove,
      totalCleaned: keysToRemove.length + specificKeysToRemove.length
    });
    
  } catch (error) {
    logger.error('Error during localStorage cleanup:', error);
  }
};

/**
 * Clean up all localStorage entries (nuclear option)
 * 
 * Use this only in extreme cases or for debugging.
 * This will remove ALL localStorage data including theme preferences.
 */
export const clearAllLocalStorage = (): void => {
  if (typeof window === 'undefined') {
    logger.warn('Window not available, skipping localStorage clear');
    return;
  }

  try {
    const itemCount = safeLocalStorageLength();
    safeLocalStorageClear();
    logger.warn('Cleared ALL localStorage entries', { itemCount });
  } catch (error) {
    logger.error('Error clearing localStorage:', error);
  }
};

/**
 * Get a summary of current localStorage usage
 * 
 * Useful for debugging and understanding what data is persisted.
 */
export const getLocalStorageSummary = (): { keys: string[]; sensitive: string[]; total: number } => {
  if (typeof window === 'undefined') {
    return { keys: [], sensitive: [], total: 0 };
  }

  const keys: string[] = [];
  const sensitive: string[] = [];
  
  for (let i = 0; i < safeLocalStorageLength(); i++) {
    const key = safeLocalStorageKey(i);
    if (key) {
      keys.push(key);
      
      // Identify potentially sensitive keys
      if (key.startsWith('activeRole_') || 
          key.startsWith('userPrefs_') ||
          key.startsWith('orgSelection_')) {
        sensitive.push(key);
      }
    }
  }
  
  return {
    keys,
    sensitive,
    total: keys.length
  };
}; 
